import { Table, TableHead, TableBody, TableRow, TableCell } from '@mui/material';
import { useSnackbar } from 'notistack';
import React from 'react';
import { useQueryClient } from 'react-query';
import { CerebroLinkButton } from '../../../shared/components';
import { useApi } from '../../../shared/context';
import { QueryKeys } from '../../../shared/hooks/Queries';
import { DataRoomUser, TeamMember, AssociatedTeamMember } from '../../../shared/models';
import { CerebroApiEndpoints } from '../../../shared/utils/api';
import { getResponseData } from '../../../shared/utils/axios';

interface AvailableTeamMemberListProps {
	dataRoomGuid: string | undefined;
	user: DataRoomUser | null | undefined;
	teamMembers: TeamMember[];
	close: () => void;
}

export const AvailableTeamMemberList: React.FC<AvailableTeamMemberListProps> = ({
	dataRoomGuid,
	user,
	teamMembers,
	close,
}) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const client = useQueryClient();
	const [memberList, setMemberList] = React.useState<TeamMember[]>(teamMembers);

	const associateToDataRoom = async (teamMember: TeamMember) => {
		await getResponseData<AssociatedTeamMember>(
			api.post(getAssociateToDataRoomApiUrl(dataRoomGuid, user?.guid, teamMember))
		);

		snackbar.enqueueSnackbar('Team member was added to data room.', { variant: 'success' });
		const index = memberList.findIndex(m => m.guid == teamMember.guid);
		setMemberList(memberList.splice(index, 1));
		Promise.all([
			client.invalidateQueries(QueryKeys.DataRoomUsers.Root(dataRoomGuid)),
			client.invalidateQueries(QueryKeys.Milestones.Root(dataRoomGuid)),
			client.invalidateQueries(QueryKeys.DataRoomUsers.TeamMembersByRoom(dataRoomGuid)),
			client.invalidateQueries(QueryKeys.DataRoomUsers.LenderByUser(dataRoomGuid, user?.userGuid)),
		]).finally(() => {
			close();
		});
	};

	return (
		<Table>
			<TableHead>
				<TableRow>
					<TableCell>Name</TableCell>
					<TableCell>Email</TableCell>
					<TableCell></TableCell>
				</TableRow>
			</TableHead>
			<TableBody>
				{memberList.map(member => {
					return (
						<TableRow key={member.guid}>
							<TableCell>
								{member.firstName} {member.lastName}
							</TableCell>
							<TableCell>{member.emailAddress}</TableCell>
							<TableCell>
								{member.isActive && (
									<CerebroLinkButton onClick={() => associateToDataRoom(member)}>
										Associate
									</CerebroLinkButton>
								)}
							</TableCell>
						</TableRow>
					);
				})}
				{memberList.length == 0 && (
					<TableRow>
						<TableCell colSpan={3} sx={{ textAlign: 'center' }}>
							Lender has no team members to add to the room, add one below.
						</TableCell>
					</TableRow>
				)}
			</TableBody>
		</Table>
	);
};

function getAssociateToDataRoomApiUrl(
	dataRoomGuid: string | undefined,
	userGuid: string | undefined,
	teamMember: TeamMember
): string {
	if (teamMember.isAssociatedContact) {
		return CerebroApiEndpoints.DataRoomUsers.AssociateContact(dataRoomGuid, userGuid, teamMember.guid);
	}
	return CerebroApiEndpoints.DataRoomUsers.Associate(dataRoomGuid, userGuid, teamMember.guid);
}
